import request from "superagent";
import _ from "lodash";

import config from "../config/app";
import session from "../config/session";
import { checkAndHandleMaxPageSize } from "../utils/AppUtils";
import StreamResponse from "../components/utils/StreamResponse";

function getCompressedResponse(data) {
  return new Response(
    new Blob([JSON.stringify(data)], { type: "application/json" })
      .stream()
      .pipeThrough(new CompressionStream("gzip"))
  );
}

const API = {
  get(url, data, token) {
    const req = request.get(`${config.api}/${url}`);
    if (token) {
      req.set("Authorization", `Bearer ${token}`);
    }

    if (config.demoMode) {
      req.set("X-Showit-App-Mode", "demo");
    }

    return req.accept("json").query(data).then(responseHandler).catch(errorHandler);
  },

  getStream(url, data, token) {
    const requestUrl = `${config.api}/${url}`;
    const options = { headers: { Accept: "json" } };
    if (token) {
      options.headers.Authorization = `Bearer ${token}`;
    }

    if (config.demoMode) {
      options.headers["X-Showit-App-Mode"] = "demo";
    }

    return fetch(requestUrl, options)
      .then((response) => response.body)
      .then(StreamResponse)
      .catch(errorHandler);
  },

  post: function (url, data, token, headers = []) {
    var req = request.post(config.api + "/" + url);
    if (token) {
      req.set("Authorization", "Bearer " + token);
    }

    if (config.demoMode) {
      req.set("X-Showit-App-Mode", "demo");
    }

    if (headers.length > 0) {
      headers.forEach((header) => {
        req.set(header.name, header.value);
      });
    }

    return req
      .set("Content-Type", "application/json")
      .send(data)
      .then(responseHandler)
      .catch(errorHandler);
  },

  put: function (url, data, token) {
    var req = request.put(config.api + "/" + url);
    if (token) {
      req.set("Authorization", "Bearer " + token);
    }

    if (config.demoMode) {
      req.set("X-Showit-App-Mode", "demo");
    }

    return req
      .set("Content-Type", "application/json")
      .send(data)
      .then(responseHandler)
      .catch(errorHandler);
  },

  patch: function (url, data, token) {
    var req = request.patch(config.api + "/" + url);
    if (token) {
      req.set("Authorization", "Bearer " + token);
    }

    if (config.demoMode) {
      req.set("X-Showit-App-Mode", "demo");
    }

    return req
      .set("Content-Type", "application/json")
      .send(data)
      .then(responseHandler)
      .catch(errorHandler);
  },

  del: function (url, data, token) {
    var req = request.del(config.api + "/" + url);
    if (token) {
      req.set("Authorization", "Bearer " + token);
    }

    if (config.demoMode) {
      req.set("X-Showit-App-Mode", "demo");
    }

    return req
      .set("Content-Type", "application/json")
      .send(data)
      .then(responseHandler)
      .catch(errorHandler);
  },

  getDesignJSON: function (url) {
    return this.getJSON(config.designsBucket + "/" + url, Date.now());
  },

  getJSON: function (url, timestamp) {
    var req = request.get(`${config.awsURL}/${url}?t=${Date.parse(new Date())}`);

    if (config.demoMode) {
      req.set("X-Showit-App-Mode", "demo");
    }

    return req.accept("json").query({ t: timestamp }).then(jsonResponseHandler).catch(errorHandler);
  },

  /**
   * Saves a design file
   * @type {import("@/managers/DesignFile").SaveFunction}
   */
  saveDesignFile: function (designKey, data) {
    const url = "designs/" + designKey;

    return getCompressedResponse(data)
      .blob()
      .then((compressedData) => {
        return API.post(url, compressedData, session.token, [
          { name: "Content-Encoding", value: "gzip" },
        ]);
      });
  },

  prepareUpload: function (data) {
    return API.post("useruploads", data, session.token);
  },

  s3Upload: function (upload, file, progressHandler) {
    var req = request.put(upload.url);
    req.on("progress", progressHandler);

    return req.set(upload.headers).send(file);
  },

  /**
   * @typedef {Object} CompleteUploadResponse
   * @property {string} asset_type
   */
  /**
   * @param {string} uploadId
   * @param {Object} data
   * @returns {Promise<CompleteUploadResponse>}
   */
  completeUpload: function (uploadId, data) {
    return API.post("useruploads/" + uploadId + "/complete", data, session.token);
  },
};

function errorHandler(err) {
  var error = {};
  if (!_.isEmpty(err.response)) {
    if (_.isEmpty(err.response.body)) {
      error.message = err.message;
      error.status = err.status;

      if (err.status == 404) {
        error.message = error.message + " [" + err.response.req.url + "]";
      }
    } else {
      error = err.response.body;

      // Callers branch on the HTTP status (e.g. useBdowLogin's 404 check); a JSON error body rarely carries one.
      if (_.isObject(error)) {
        if (error.status == null) {
          error.status = err.status;
        }
      } else {
        // Non-object body (a bare JSON string/number) — can't carry a status, so wrap it.
        error = { message: String(error), status: err.status };
      }
    }
  } else {
    //if no response then this is network and no status is available.
    if (_.has(err, "message")) {
      error.message = err.message;
    } else {
      try {
        error.message = JSON.stringify(err);
      } catch (e) {
        error.message = "Critical Error";
      }
    }

    error.status = null;
  }

  return Promise.reject(error);
}

function jsonResponseHandler(response) {
  const result = {
    data: response.body,
  };

  if (_.has(response, ["header", "etag"])) {
    result.eTag = _.trim(response.header.etag, '"');
  }

  if (_.has(response, ["header", "content-length"])) {
    checkAndHandleMaxPageSize(result.data.name, response.header["content-length"]);
  }

  return Promise.resolve(result);
}

function responseHandler(response) {
  return Promise.resolve(response.body);
}

export default API;
